import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API, errMsg, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Archive, ArrowLeft, ChevronLeft, ChevronRight, Database, Download, FileText,
  Film, HardDrive, Image as ImageIcon, Layers, Mic, Music, PlaySquare, Plus,
  RefreshCcw, Search, Sticker, Trash2, TriangleAlert, X,
} from "lucide-react";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const KIND_ICONS = {
  photo: ImageIcon, video: Film, video_note: PlaySquare, animation: Film,
  audio: Music, voice: Mic, sticker: Sticker, document: FileText,
};

const mediaUrl = (id, download = false) =>
  `${API}/vault/media/${id}?token=${encodeURIComponent(getToken() || "")}${download ? "&download=true" : ""}`;

const Stat = ({ label, value, icon: Icon, accent, tint }) => (
  <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] border-l-4 p-4 flex items-center gap-3"
        style={{ borderLeftColor: accent || "var(--tg-faint)" }}>
    <span className="tint-chip h-9 w-9"
          style={{ background: tint || "rgba(140,147,166,.12)", color: accent || "var(--tg-faint)" }}>
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <div className="label-caps">{label}</div>
      <div className="font-head font-bold text-xl tabular-nums truncate">{value}</div>
    </div>
  </Card>
);

function MediaPreview({ msg }) {
  const [open, setOpen] = useState(false);
  const kind = msg.media_kind;
  if (!msg.media_id) {
    if (!kind || kind === "webpage") return null;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-[var(--c-amber)] border border-[rgb(var(--c-amber-rgb)/0.30)] rounded px-1.5 py-0.5"
            title={msg.media_pending ? "Media download pending repair" : "Media not stored"}>
        <TriangleAlert className="h-2.5 w-2.5" /> {kind} {msg.media_pending ? "pending" : "not stored"}
      </span>
    );
  }
  if (kind === "photo" || kind === "sticker") {
    return (
      <>
        <img src={mediaUrl(msg.media_id)} alt="" loading="lazy"
             onClick={() => setOpen(true)}
             className="h-14 w-14 object-cover rounded-md border border-[var(--tg-border)] cursor-zoom-in" />
        {open && (
          <div className="fixed inset-0 z-[100] bg-black/85 grid place-items-center p-6"
               onClick={() => setOpen(false)}>
            <img src={mediaUrl(msg.media_id)} alt="" className="max-h-full max-w-full rounded-lg" />
            <button className="absolute top-4 right-4 text-[var(--tg-muted)] hover:text-[var(--tg-strong)]"><X className="h-6 w-6" /></button>
          </div>
        )}
      </>
    );
  }
  if (kind === "video" || kind === "animation" || kind === "video_note") {
    return (
      <video src={mediaUrl(msg.media_id)} controls preload="none"
             className="h-20 max-w-[180px] rounded-md border border-[var(--tg-border)] bg-black" />
    );
  }
  if (kind === "audio" || kind === "voice") {
    return <audio src={mediaUrl(msg.media_id)} controls preload="none" className="h-8 max-w-[200px]" />;
  }
  return (
    <a href={mediaUrl(msg.media_id, true)} target="_blank" rel="noreferrer"
       className="inline-flex items-center gap-1.5 text-xs text-[var(--c-sky)] hover:underline">
      <Download className="h-3 w-3" /> {kind || "file"}
    </a>
  );
}

function ArchiveDialog({ open, onOpenChange, accounts, sources, onCreated }) {
  const empty = { name: "", account_id: "", source: "", from_date: "", to_date: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const submit = async (start) => {
    if (!form.name || !form.account_id || !form.source)
      return toast.error("Name, account and source channel are required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        from_date: form.from_date ? new Date(form.from_date).toISOString() : null,
        to_date: form.to_date ? new Date(form.to_date).toISOString() : null,
      };
      const { data: job } = await api.post("/jobs/archive", payload);
      if (start) await api.post(`/jobs/${job.id}/start`);
      toast.success(start ? "Archive capture started" : "Archive job created");
      setForm(empty); onOpenChange(false); onCreated();
    } catch (e) { toast.error(errMsg(e, "Failed to create archive job")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">Archive a Channel</DialogTitle>
          <DialogDescription className="text-[var(--tg-faint)]">
            Capture a source channel's full history into the local Content Vault.
            Once captured, you can rebuild it into any new channel later — even if the
            original source disappears.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="label-caps">Job Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Archive: News Channel" data-testid="input-archive-name"
                   className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)]" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Account (member of the source)</Label>
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger data-testid="select-archive-account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: a.authorized ? "var(--c-emerald)" : "var(--c-rose)" }} />
                      {a.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Source Channel</Label>
            {sources.length > 0 ? (
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="select-archive-source" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
                  {sources.map((c) => (
                    <SelectItem key={c.id} value={c.username ? `@${c.username}` : c.resolved_id || c.identifier}>{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                   placeholder="@channel or -100123456789" data-testid="input-archive-source"
                   className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="label-caps">From Date (optional)</Label>
              <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                     className="bg-[var(--tg-bg)] border-[var(--tg-border)]" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">To Date (optional)</Label>
              <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                     className="bg-[var(--tg-bg)] border-[var(--tg-border)]" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button disabled={busy} onClick={() => submit(true)} data-testid="archive-create-start"
                    className="flex-1 bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold">
              Create & Start
            </Button>
            <Button disabled={busy} onClick={() => submit(false)} variant="outline"
                    className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]">Create only</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReplayDialog({ open, onOpenChange, channel, accounts, dests, rules, templates, onCreated }) {
  const empty = {
    name: "", account_id: "", destinations: [], copy_style: "branded",
    rule_ids: [], template_ids: [], min_id: "", max_id: "", from_date: "",
    to_date: "", replay_delay: "2", include_deleted: false,
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [createdDests, setCreatedDests] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (open && channel) setForm((f) => ({ ...f, name: f.name || `Replay: ${channel.title || channel.source_ref || "archive"}` }));
  }, [open, channel]);
  const toggle = (field, id) => setForm((f) => ({
    ...f, [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id],
  }));
  const createChannel = async () => {
    if (!form.account_id) return toast.error("Pick the posting account first — it will own the new channel");
    if (!newTitle.trim()) return toast.error("Enter a name for the new channel");
    setCreating(true);
    try {
      const { data } = await api.post("/channels/create", {
        account_id: form.account_id, title: newTitle.trim(), role: "destination",
      });
      setCreatedDests((list) => [...list, data]);
      setForm((f) => ({ ...f, destinations: [...f.destinations, data.resolved_id || data.identifier] }));
      setNewTitle("");
      toast.success(`Created new channel "${data.title}" and selected it`);
    } catch (e) { toast.error(errMsg(e, "Failed to create the channel")); }
    finally { setCreating(false); }
  };
  const submit = async (start) => {
    if (!form.name || !form.account_id || !form.destinations.length)
      return toast.error("Name, account and at least one destination are required");
    setBusy(true);
    try {
      const payload = {
        name: form.name, account_id: form.account_id,
        vault_channel_id: channel.id, destinations: form.destinations,
        copy_style: form.copy_style,
        rule_ids: form.rule_ids.length ? form.rule_ids : null,
        template_ids: form.template_ids.length ? form.template_ids : null,
        min_id: form.min_id ? parseInt(form.min_id, 10) : null,
        max_id: form.max_id ? parseInt(form.max_id, 10) : null,
        from_date: form.from_date ? new Date(form.from_date).toISOString() : null,
        to_date: form.to_date ? new Date(form.to_date).toISOString() : null,
        replay_delay: form.replay_delay ? parseFloat(form.replay_delay) : null,
        include_deleted: form.include_deleted,
      };
      const { data: job } = await api.post("/jobs/replay", payload);
      if (start) await api.post(`/jobs/${job.id}/start`);
      toast.success(start ? "Replay started — track it on Copy Jobs" : "Replay job created");
      setForm(empty); onOpenChange(false); onCreated();
    } catch (e) { toast.error(errMsg(e, "Failed to create replay job")); }
    finally { setBusy(false); }
  };
  if (!channel) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">Replay Archive → New Channel</DialogTitle>
          <DialogDescription className="text-[var(--tg-faint)]">
            Rebuild "{channel.title || channel.source_ref}" chronologically from the local
            archive ({channel.message_count} messages). The original source is not needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="label-caps">Job Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="input-replay-name"
                   className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)]" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Posting Account (admin of destination)</Label>
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger data-testid="select-replay-account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: a.authorized ? "var(--c-emerald)" : "var(--c-rose)" }} />
                      {a.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Destination Channels — {form.destinations.length} selected</Label>
            <div className="flex gap-2">
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                     placeholder="…or create a brand-new channel: name" data-testid="input-new-channel-title"
                     className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-emerald)]" />
              <Button type="button" disabled={creating} onClick={createChannel} data-testid="create-channel-btn"
                      className="shrink-0 bg-[rgb(var(--c-emerald-rgb)/0.15)] text-[var(--c-emerald)] hover:bg-[rgb(var(--c-emerald-rgb)/0.25)] border border-[rgb(var(--c-emerald-rgb)/0.30)] font-semibold">
                <Plus className="h-3.5 w-3.5 mr-1" /> Create
              </Button>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto border border-[var(--tg-border)] rounded-md p-2 bg-[var(--tg-bg)]">
              {dests.length === 0 && createdDests.length === 0 &&
                <p className="text-xs text-[var(--tg-faint)] px-1">No destinations yet — create a brand-new channel above, or add one on the Channels page.</p>}
              {[...createdDests, ...dests.filter((c) => !createdDests.some((n) => n.id === c.id))].map((c) => {
                const val = c.username ? `@${c.username}` : c.resolved_id || c.identifier;
                const checked = form.destinations.includes(val);
                return (
                  <button key={c.id} type="button" onClick={() => toggle("destinations", val)}
                          data-testid={`replay-dest-${c.id}`}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded text-sm transition-colors ${checked ? "bg-[rgb(var(--c-sky-rgb)/0.10)] text-[var(--c-sky)]" : "hover:bg-[var(--tg-hover)]"}`}>
                    <span className="truncate flex-1 text-left">{c.title}</span>
                    <span className={`h-4 w-4 rounded border grid place-items-center shrink-0 ${checked ? "bg-[var(--c-sky)] border-[var(--c-sky)]" : "border-[var(--tg-border-hi)]"}`}>
                      {checked && <span className="h-1.5 w-1.5 bg-white rounded-sm" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Copy Style</Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, copy_style: "branded" })}
                      className={`text-left rounded-md border p-2.5 transition-colors ${form.copy_style === "branded" ? "border-[var(--c-sky)] bg-[rgb(var(--c-sky-rgb)/0.10)]" : "border-[var(--tg-border)] hover:bg-[var(--tg-hover)]"}`}>
                <div className="font-semibold text-sm">Branded</div>
                <div className="text-[10px] text-[var(--tg-faint)]">Rules + watermark applied</div>
              </button>
              <button type="button" onClick={() => setForm({ ...form, copy_style: "clone" })}
                      className={`text-left rounded-md border p-2.5 transition-colors ${form.copy_style === "clone" ? "border-[var(--c-teal)] bg-[rgb(var(--c-teal-rgb)/0.10)]" : "border-[var(--tg-border)] hover:bg-[var(--tg-hover)]"}`}>
                <div className="font-semibold text-sm">Clone</div>
                <div className="text-[10px] text-[var(--tg-faint)]">Exact stored content</div>
              </button>
            </div>
          </div>
          {form.copy_style === "branded" && (rules.length > 0 || templates.length > 0) && (
            <div className="space-y-2">
              <Label className="label-caps">Rules & Templates (optional)</Label>
              <div className="space-y-1.5 max-h-36 overflow-y-auto border border-[var(--tg-border)] rounded-md p-2 bg-[var(--tg-bg)]">
                {rules.map((r) => {
                  const checked = form.rule_ids.includes(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggle("rule_ids", r.id)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded text-xs font-mono transition-colors ${checked ? "bg-[rgb(var(--c-sky-rgb)/0.10)] text-[var(--c-sky)]" : "hover:bg-[var(--tg-hover)]"}`}>
                      <span className="truncate flex-1 text-left">{r.old_value} → {r.new_value || "(removed)"}</span>
                      <span className={`h-3.5 w-3.5 rounded border grid place-items-center shrink-0 ${checked ? "bg-[var(--c-sky)] border-[var(--c-sky)]" : "border-[var(--tg-border-hi)]"}`} />
                    </button>
                  );
                })}
                {templates.map((t) => {
                  const checked = form.template_ids.includes(t.id);
                  return (
                    <button key={t.id} type="button" onClick={() => toggle("template_ids", t.id)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded text-xs transition-colors ${checked ? "bg-[rgb(var(--c-violet-rgb)/0.10)] text-[var(--c-violet)]" : "hover:bg-[var(--tg-hover)]"}`}>
                      <span className="truncate flex-1 text-left"><Layers className="h-3 w-3 inline mr-1" />{t.name} ({t.rules?.length || 0})</span>
                      <span className={`h-3.5 w-3.5 rounded border grid place-items-center shrink-0 ${checked ? "bg-[var(--c-violet)] border-[var(--c-violet)]" : "border-[var(--tg-border-hi)]"}`} />
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[var(--tg-faint)]">Leave all unselected to apply every enabled rule.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="label-caps">From Date (optional)</Label>
              <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="bg-[var(--tg-bg)] border-[var(--tg-border)]" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">To Date (optional)</Label>
              <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="bg-[var(--tg-bg)] border-[var(--tg-border)]" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">After Msg ID</Label>
              <Input value={form.min_id} onChange={(e) => setForm({ ...form, min_id: e.target.value })} placeholder="0" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Up to Msg ID</Label>
              <Input value={form.max_id} onChange={(e) => setForm({ ...form, max_id: e.target.value })} placeholder="∞" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Seconds Between Posts (replay speed)</Label>
            <Input value={form.replay_delay} onChange={(e) => setForm({ ...form, replay_delay: e.target.value })}
                   placeholder="2" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" />
            <p className="text-[11px] text-[var(--tg-faint)]">Higher = slower and safer against Telegram rate limits. 2–5s recommended for large archives.</p>
          </div>
          <button type="button" onClick={() => setForm({ ...form, include_deleted: !form.include_deleted })}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition-colors ${form.include_deleted ? "border-[rgb(var(--c-amber-rgb)/0.40)] bg-[rgb(var(--c-amber-rgb)/0.10)] text-[var(--c-amber)]" : "border-[var(--tg-border)] hover:bg-[var(--tg-hover)] text-[var(--tg-muted)]"}`}>
            <span>Include messages deleted at the source</span>
            <span className={`h-4 w-4 rounded border grid place-items-center ${form.include_deleted ? "bg-[var(--c-amber)] border-[var(--c-amber)]" : "border-[var(--tg-border-hi)]"}`}>
              {form.include_deleted && <span className="h-1.5 w-1.5 bg-black rounded-sm" />}
            </span>
          </button>
          <div className="flex gap-2">
            <Button disabled={busy} onClick={() => submit(true)} data-testid="replay-create-start"
                    className="flex-1 bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold">Create & Start Replay</Button>
            <Button disabled={busy} onClick={() => submit(false)} variant="outline" className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]">Create only</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MessageBrowser({ channel, onBack, onReplay }) {
  const [page, setPage] = useState({ total: 0, messages: [] });
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [order, setOrder] = useState("desc");
  const [loading, setLoading] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vault/channels/${channel.id}/messages`, {
        params: {
          offset, limit, order,
          search: query || undefined,
          media_kind: kind === "all" ? undefined : kind,
        },
      });
      setPage(data);
    } catch (e) { toast.error(errMsg(e, "Failed to load archive")); }
    finally { setLoading(false); }
  }, [channel.id, offset, query, kind, order]);

  useEffect(() => { load(); }, [load]);

  const pageNum = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(page.total / limit));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="vault-back">
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Archives
        </Button>
        <div className="min-w-0">
          <div className="font-head font-bold text-lg truncate">{channel.title || channel.source_ref}</div>
          <div className="text-xs text-[var(--tg-faint)] font-mono">
            {channel.username ? `@${channel.username} · ` : ""}{channel.message_count} messages · {fmtBytes(channel.storage_bytes)}
          </div>
        </div>
        <div className="ml-auto">
          <Button size="sm" onClick={() => onReplay(channel)} data-testid="vault-replay-from-browser"
                  className="bg-[rgb(var(--c-emerald-rgb)/0.15)] text-[var(--c-emerald)] hover:bg-[rgb(var(--c-emerald-rgb)/0.25)] border border-[rgb(var(--c-emerald-rgb)/0.30)] font-semibold">
            <PlaySquare className="h-3.5 w-3.5 mr-1.5" /> Replay to Channel
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form className="relative flex-1 min-w-[220px]" onSubmit={(e) => { e.preventDefault(); setOffset(0); setQuery(search.trim()); }}>
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tg-faint)]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search message text… (press Enter)"
                 data-testid="vault-search"
                 className="bg-[var(--tg-bg)] border-[var(--tg-border)] pl-9 focus:border-[var(--c-sky)]" />
        </form>
        <Select value={kind} onValueChange={(v) => { setOffset(0); setKind(v); }}>
          <SelectTrigger className="w-36 bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="vault-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="text">Text only</SelectItem>
            <SelectItem value="photo">Photos</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="audio">Audio</SelectItem>
            <SelectItem value="voice">Voice</SelectItem>
            <SelectItem value="sticker">Stickers</SelectItem>
            <SelectItem value="animation">GIFs</SelectItem>
            <SelectItem value="webpage">Link previews</SelectItem>
            <SelectItem value="poll">Polls</SelectItem>
          </SelectContent>
        </Select>
        <Select value={order} onValueChange={(v) => { setOffset(0); setOrder(v); }}>
          <SelectTrigger className="w-32 bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
            <SelectItem value="desc">Newest first</SelectItem>
            <SelectItem value="asc">Oldest first</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" title="Refresh">
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] overflow-hidden">
        {page.messages.length === 0 ? (
          <div className="p-12 text-center text-[var(--tg-faint)] text-sm" data-testid="vault-no-messages">
            {loading ? "Loading…" : query ? "No archived messages match this search." : "Nothing archived yet — messages appear here as they are captured."}
          </div>
        ) : (
          <div className="divide-y divide-[var(--tg-line-soft)]">
            {page.messages.map((m) => {
              const Icon = KIND_ICONS[m.media_kind] || null;
              return (
                <div key={m.msg_id} className={`p-4 flex gap-4 ${m.deleted ? "opacity-60" : ""}`} data-testid={`vault-msg-${m.msg_id}`}>
                  <div className="w-24 shrink-0">
                    <div className="font-mono text-xs text-[var(--c-sky)]">#{m.msg_id}</div>
                    <div className="text-[10px] text-[var(--tg-faint)] mt-0.5">{fmtDateTime(m.date)}</div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {m.deleted ? <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-[rgb(var(--c-rose-rgb)/0.15)] text-[var(--c-rose)]">deleted</span> : null}
                      {m.edit_history ? <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-[rgb(var(--c-amber-rgb)/0.15)] text-[var(--c-amber)]">edited</span> : null}
                      {m.grouped_id ? <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-[rgb(var(--c-violet-rgb)/0.15)] text-[var(--c-violet)]">album</span> : null}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    {m.media_kind && (
                      <div className="flex items-center gap-2 text-xs text-[var(--tg-muted)]">
                        {Icon && <Icon className="h-3.5 w-3.5 text-[var(--tg-faint)]" />}
                        <MediaPreview msg={m} />
                      </div>
                    )}
                    {m.text && <div className="text-sm text-[var(--tg-ink)] whitespace-pre-wrap break-words leading-relaxed">{m.text.length > 600 ? `${m.text.slice(0, 600)}…` : m.text}</div>}
                    {!m.text && !m.media_kind && <div className="text-xs text-[var(--tg-faint)] italic">{m.service ? "Service message" : "(empty)"}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <div className="text-xs text-[var(--tg-faint)] tabular-nums" data-testid="vault-pagination-info">
          {page.total ? `${offset + 1}–${Math.min(offset + limit, page.total)} of ${page.total}` : "0 messages"}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
                  className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="vault-prev">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-[var(--tg-muted)] tabular-nums">{pageNum} / {pages}</span>
          <Button variant="outline" size="sm" disabled={offset + limit >= page.total} onClick={() => setOffset(offset + limit)}
                  className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="vault-next">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function StoragePanel({ storage }) {
  if (!storage) return null;
  const freePct = storage.disk_total_bytes
    ? Math.round((storage.disk_free_bytes / storage.disk_total_bytes) * 100) : null;
  const lowDisk = freePct !== null && freePct < 10;
  const items = [
    { label: "Vault database", value: fmtBytes(storage.db_size_bytes) },
    { label: "Media store", value: fmtBytes(storage.storage_bytes) },
    { label: "Free disk", value: `${fmtBytes(storage.disk_free_bytes)}${freePct !== null ? ` (${freePct}%)` : ""}`, alert: lowDisk },
    { label: "Pending media", value: storage.media_pending, alert: storage.media_pending > 0,
      hint: "messages archived without their file yet — repaired by the next archive run" },
    { label: "Orphan media", value: `${storage.orphan_media_files} · ${fmtBytes(storage.orphan_media_bytes)}`,
      hint: "files no archived message references (kept — nothing is auto-deleted)" },
    { label: "Last archive event", value: storage.last_archive_event ? fmtDateTime(storage.last_archive_event) : "—" },
  ];
  return (
    <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-4" data-testid="vault-storage-panel">
      <div className="label-caps mb-3">Storage Safety</div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {items.map((item) => (
          <div key={item.label} title={item.hint} className="min-w-0">
            <div className="text-[10px] text-[var(--tg-faint)] uppercase tracking-wider truncate">{item.label}</div>
            <div className={`font-head font-semibold text-sm mt-0.5 tabular-nums truncate ${item.alert ? "text-[var(--c-amber)]" : ""}`}>
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Vault() {
  const [stats, setStats] = useState(null);
  const [storage, setStorage] = useState(null);
  const [channels, setChannels] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [browsing, setBrowsing] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [replayFor, setReplayFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, c, a, ch, r, t] = await Promise.all([
        api.get("/vault/stats"), api.get("/vault/channels"), api.get("/accounts"),
        api.get("/channels"), api.get("/rules"), api.get("/rule-templates"),
      ]);
      setStats(s.data); setChannels(c.data); setAccounts(a.data);
      setAllChannels(ch.data); setRules(r.data); setTemplates(t.data);
    } catch (e) { console.error("vault load failed", e); }
  }, []);

  const loadStorage = useCallback(async () => {
    try { setStorage((await api.get("/vault/storage")).data); }
    catch (e) { console.error("vault storage load failed", e); }
  }, []);

  useEffect(() => {
    load();
    loadStorage();
    const t = setInterval(load, 15000);
    const ts = setInterval(loadStorage, 60000);
    return () => { clearInterval(t); clearInterval(ts); };
  }, [load, loadStorage]);

  const sources = useMemo(() => allChannels.filter((c) => c.role === "source"), [allChannels]);
  const dests = useMemo(() => allChannels.filter((c) => c.role === "destination"), [allChannels]);

  const removeArchive = async (channel) => {
    if (!window.confirm(`Permanently delete the local archive of "${channel.title || channel.source_ref}" (${channel.message_count} messages)? This cannot be undone.`)) return;
    try {
      await api.delete(`/vault/channels/${channel.id}`);
      toast.success("Archive deleted");
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to delete archive")); }
  };

  if (browsing) {
    const current = channels.find((c) => c.id === browsing.id) || browsing;
    return (
      <div className="w-full max-w-[1700px] space-y-6">
        <MessageBrowser channel={current} onBack={() => setBrowsing(null)} onReplay={(c) => setReplayFor(c)} />
        <ReplayDialog open={!!replayFor} onOpenChange={(v) => !v && setReplayFor(null)} channel={replayFor}
                      accounts={accounts} dests={dests} rules={rules} templates={templates} onCreated={load} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1700px] space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="label-caps">Content Vault ({channels.length} archived channel{channels.length === 1 ? "" : "s"})</div>
        <Button onClick={() => setArchiveOpen(true)} className="bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="new-archive-btn">
          <Plus className="h-4 w-4 mr-2" /> Archive a Channel
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Archived Channels" value={stats?.channels ?? "—"} icon={Archive} accent="var(--c-sky)" tint="var(--t-sky)" />
        <Stat label="Messages Stored" value={stats?.messages?.toLocaleString() ?? "—"} icon={Database} accent="var(--c-emerald)" tint="var(--t-emerald)" />
        <Stat label="Media Files" value={stats?.media_files?.toLocaleString() ?? "—"} icon={ImageIcon} accent="var(--c-violet)" tint="var(--t-violet)" />
        <Stat label="Storage Used" value={stats ? fmtBytes(stats.storage_bytes) : "—"} icon={HardDrive} accent="var(--c-amber)" tint="var(--t-amber)" />
      </div>

      <StoragePanel storage={storage} />

      {channels.length === 0 ? (
        <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-12 text-center" data-testid="vault-empty">
          <Archive className="h-8 w-8 mx-auto text-[var(--tg-faint)]" />
          <div className="font-head font-bold text-xl mt-4">Nothing archived yet</div>
          <p className="text-[var(--tg-faint)] mt-2 text-sm max-w-md mx-auto">
            Running Live and Historical jobs archive their sources here automatically.
            Or capture a channel's complete history directly — once stored, you can rebuild
            it into any future channel even if the source disappears.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {channels.map((c) => (
            <Card key={c.id} className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5 hover:border-[var(--tg-border-hi)] transition-colors" data-testid={`vault-channel-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-head font-semibold truncate">{c.title || c.source_ref || `Archive #${c.id}`}</div>
                  <div className="text-xs text-[var(--tg-faint)] font-mono truncate mt-0.5">
                    {c.username ? `@${c.username}` : c.source_ref}
                    {c.account_name ? <span> · via {c.account_name}</span> : null}
                  </div>
                </div>
                <button onClick={() => removeArchive(c)} className="text-[var(--tg-faint)] hover:text-[var(--c-rose)] transition-colors shrink-0" title="Delete archive" data-testid={`vault-delete-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div>
                  <div className="font-head font-bold text-2xl tabular-nums">{c.message_count.toLocaleString()}</div>
                  <div className="text-[10px] text-[var(--tg-faint)] uppercase tracking-wider">messages</div>
                </div>
                <div>
                  <div className="font-head font-bold text-2xl tabular-nums">{c.media_count.toLocaleString()}</div>
                  <div className="text-[10px] text-[var(--tg-faint)] uppercase tracking-wider">media</div>
                </div>
                <div>
                  <div className="font-head font-bold text-2xl tabular-nums">{fmtBytes(c.storage_bytes)}</div>
                  <div className="text-[10px] text-[var(--tg-faint)] uppercase tracking-wider">storage</div>
                </div>
              </div>
              <div className="text-[11px] text-[var(--tg-faint)] mt-3">
                {fmtDate(c.first_date)} → {fmtDate(c.last_date)}
                {c.last_archived_at ? <span> · last capture {fmtDateTime(c.last_archived_at)}</span> : null}
                {c.deleted_count ? <span className="text-[var(--c-rose)]"> · {c.deleted_count} deleted at source</span> : null}
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => setBrowsing(c)} data-testid={`vault-browse-${c.id}`}
                        className="flex-1 border-[var(--tg-border)] hover:bg-[var(--tg-hover)] font-semibold">
                  <Search className="h-3.5 w-3.5 mr-1.5" /> Browse
                </Button>
                <Button size="sm" onClick={() => setReplayFor(c)} data-testid={`vault-replay-${c.id}`}
                        className="flex-1 bg-[rgb(var(--c-emerald-rgb)/0.15)] text-[var(--c-emerald)] hover:bg-[rgb(var(--c-emerald-rgb)/0.25)] border border-[rgb(var(--c-emerald-rgb)/0.30)] font-semibold">
                  <PlaySquare className="h-3.5 w-3.5 mr-1.5" /> Replay
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ArchiveDialog open={archiveOpen} onOpenChange={setArchiveOpen} accounts={accounts} sources={sources} onCreated={load} />
      <ReplayDialog open={!!replayFor} onOpenChange={(v) => !v && setReplayFor(null)} channel={replayFor}
                    accounts={accounts} dests={dests} rules={rules} templates={templates} onCreated={load} />
    </div>
  );
}
